import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, of } from 'rxjs';
import { map, switchMap, catchError, takeUntil } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import {
  DeviceService,
  ExceptionStreamInit,
  ExceptionStreamChunk,
  ExceptionStreamComplete,
  ExceptionTraceResponse,
} from '../../services/device.service';
import { TelemetryService } from '../../core/services/telemetry.service';
import * as ExceptionsActions from './exceptions.actions';

@Injectable()
export class ExceptionsEffects {
  private actions$ = inject(Actions);
  private deviceService = inject(DeviceService);
  private telemetry = inject(TelemetryService);

  /**
   * SSE streaming effect.
   * switchMap cancels the previous stream when a new streamExceptions action arrives.
   * takeUntil(cancel$) cancels the current stream when streamCancel is dispatched.
   */
  streamExceptions$ = createEffect(() => {
    const cancel$ = this.actions$.pipe(ofType(ExceptionsActions.streamCancel));

    return this.actions$.pipe(
      ofType(ExceptionsActions.streamExceptions),
      switchMap(({ params }) =>
        this.deviceService.streamExceptions(params).pipe(
          takeUntil(cancel$),
          map(event => {
            if (event.type === 'init') {
              return ExceptionsActions.streamInit({
                total_services: (event.data as ExceptionStreamInit).total_services,
              });
            }
            if (event.type === 'chunk') {
              const chunk = event.data as ExceptionStreamChunk;
              return ExceptionsActions.streamChunk({ exceptions: chunk.exceptions ?? [] });
            }
            const complete = event.data as ExceptionStreamComplete;
            return ExceptionsActions.streamComplete({
              total_count: complete.total_count,
              services_queried: complete.services_queried ?? [],
              services_failed: complete.services_failed ?? [],
            });
          }),
          catchError((err) => {
            const msg: string = err?.message ?? 'Failed to stream exceptions';
            this.telemetry.trackException(
              err instanceof Error ? err : new Error(msg),
              { context: 'EXCEPTIONS_STREAM_ERROR' }
            );
            return of(ExceptionsActions.streamError({ error: msg }));
          }),
        )
      ),
    );
  });

  /** One-shot HTTP effect for loading an exception trace. */
  loadTrace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExceptionsActions.loadTrace),
      switchMap(({ operationId, environment, excTimestamp }) =>
        this.deviceService.getExceptionTrace(operationId, environment, excTimestamp).pipe(
          map((response: ExceptionTraceResponse) => {
            const steps = response.steps;
            const excIdx = steps.findIndex(s => s.item_type === 'exception');
            const trimmed = excIdx >= 0 ? steps.slice(0, excIdx + 1) : steps;
            return ExceptionsActions.loadTraceSuccess({ operationId, steps: trimmed });
          }),
          catchError((err) => {
            const msg: string = err instanceof HttpErrorResponse
              ? (err.error?.detail ?? err.message)
              : (err?.message ?? 'Failed to load trace');
            this.telemetry.trackException(
              err instanceof Error ? err : new Error(String(msg)),
              { context: 'EXCEPTIONS_TRACE_ERROR', operationId }
            );
            return of(ExceptionsActions.loadTraceFailure({ operationId, error: msg }));
          }),
        )
      ),
    )
  );
}
